
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download } from "lucide-react";

interface TermsAcknowledgmentProps {
  onAcknowledgmentChange: (acknowledged: boolean) => void;
  variant?: "standard" | "pilot";
  className?: string;
}

export const TermsAcknowledgment = ({ onAcknowledgmentChange, variant = "standard", className = "" }: TermsAcknowledgmentProps) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  const handleCheckboxChange = (checked: boolean) => {
    setAcknowledged(checked);
    onAcknowledgmentChange(checked);
  };

  const isPilot = variant === "pilot";

  const downloadPdf = () => {
    const link = document.createElement('a');
    link.href = isPilot ? '/Pilot_Pack_Terms_and_Conditions.pdf' : '/Terms_and_Conditions_of_Service_V1.3.pdf';
    link.download = isPilot ? 'Pilot-Pack-Terms-and-Conditions.pdf' : 'Terms-and-Conditions-V1.3.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dialogTitle = isPilot
    ? "Pilot Pack Terms and Conditions"
    : "Terms and Conditions of Service V1.3";

  const pilotTermsContent = `SNAPSHOT LIVE – PILOT PACK TERMS

1. PILOT PACK OVERVIEW

Snapshot Live may provide a Pilot Pack to allow customers to evaluate the Snapshot Live camera and platform before entering into a full service agreement.

The Pilot Pack includes:

- One Snapshot Live camera unit
- Access to the Snapshot Live platform
- Up to 600 captured images
- A maximum pilot period of three (3) months from activation.

The Pilot Pack is provided on a "keep or return" evaluation basis.

2. PILOT PACK FEE

The customer shall pay the agreed Pilot Pack fee prior to shipment or activation of the camera.

The Pilot Pack fee covers temporary use of the camera, platform access, connectivity and the included image allowance. Unless otherwise agreed in writing, the Pilot Pack fee is non-refundable.

3. OWNERSHIP AND USE OF EQUIPMENT

All equipment supplied as part of the Pilot Pack remains the property of Snapshot Live during the pilot period unless purchased by the customer.

The customer agrees to:

- take reasonable care of the equipment
- use the equipment only for its intended purpose
- not modify, dismantle or tamper with the device.

The customer is responsible for any loss, theft or damage to the equipment during the pilot period.

4. IMAGE USAGE

The Pilot Pack includes a maximum of 600 captured images during the pilot period.

Snapshot Live reserves the right to suspend image capture or apply additional usage charges where this allowance is exceeded.

5. END OF PILOT PERIOD

The Pilot Pack will remain active for a maximum of three (3) months from activation.

At the end of the pilot period the customer must choose one of the following options:

a) Convert to a Snapshot Live contract
The customer may retain the camera and enter into a standard Snapshot Live service agreement.

b) Purchase the camera
The customer may purchase the camera hardware at the agreed price.

c) Return the equipment
The camera must be returned to Snapshot Live within fourteen (14) days of the pilot end date.

6. EQUIPMENT RETURN

Where the customer elects to return the equipment:

- the camera must be returned in good working condition (reasonable wear and tear excepted)
- the customer is responsible for return shipping unless otherwise agreed.

Failure to return the equipment within 14 days may result in the customer being charged the replacement value of the device.

7. GOVERNING TERMS

These Pilot Pack terms supplement Snapshot Live's standard Terms and Conditions, which remain applicable throughout the pilot period.

© Snapshot Live Limited 2025`;

  const standardTermsContent = `SNAPSHOT LIVE LIMITED
TERMS AND CONDITIONS OF SERVICE
V1.3 (Integrated Data Processing Agreement)

Business customers only. The Customer warrants it is acting in the course of business and not as a consumer. These Terms supersede any prior terms for the Services and Hardware.

1. INTERPRETATION

1.1 Definitions

Agreement: these Terms and Conditions of Service (including all Schedules and any Order/Quotation incorporated by reference).

Business Day: a day other than Saturday, Sunday or a public holiday in England.

Commencement Date: the earlier of (a) the date the Customer accepts a Snapshot Live quotation or order form (including by signature, email confirmation or purchase order), or (b) the date the Customer first accesses or uses the Service.

Confidential Information: this Agreement and any non‑public information of either party (including but not limited to commercial, financial, technical, product, roadmap, pricing, usage data and know‑how).

Controller/Processor/Personal Data/Processing/Personal Data Breach/Data Subject/Supervisory Authority: as defined in UK and other relevant GDPR legislation.

Customer: the business entity that enters into this Agreement and/or accesses the Service.

Customer Personal Data: Personal Data processed by Snapshot Live on the Customer's behalf under this Agreement (including still Images and associated metadata and any account data processed as Processor).

Data Protection Legislation: the UK GDPR and the Data Protection Act 2018, and any successor laws.

Delivery Date(s): dates agreed by the parties for delivery of Devices.

Device(s): Snapshot Live solar/LTE camera hardware supplied or rented to the Customer.

Force Majeure Event: an event beyond a party's reasonable control (including telecoms or cloud outage, acts of God, war, strike, epidemic, or government action).

Images: still images and associated metadata (e.g., timestamps, GNSS coordinates and system watermarks) produced by the Device(s) and/or Platform.

Intellectual Property Rights (IPR): patents, copyrights, database rights, design rights, trade marks, domain names, trade secrets and analogous rights, registered or unregistered, worldwide.

On‑Air Period: the active service period attached to a Plan during which Credits can be consumed (Schedule 3).

Operating Window: the daily time window within which the Device is scheduled to operate.

Permitted Use: as set out in clause 3.2.

Plan: the subscription plan purchased by the Customer (On‑Air Period, image allowances, features).

Platform: Snapshot Live's hosted web/app portal, APIs and analytics services (including AI models and overlays).

Service: access to the Platform, storage, analytics (including AI), reporting and support purchased via a Plan and/or Usage Credits.

Snapshot Live/Supplier: Snapshot Live Limited (company details as set out on the Order/Invoice).

Subprocessor: any third party engaged by Snapshot Live to process Customer Personal Data.

Territory: the countries where the Service is supported, set out in Schedule 2.

Usage Credits/Credits: metered units consumed by Periodic or On‑Demand image requests and by certain Platform features, as described in Schedule 3.

1.2 Headings do not affect interpretation.

1.3 Singular includes plural and vice versa, references to a person include bodies corporate and unincorporated.

1.4 "Writing" includes email.

1.5 A duty not to do something includes not permitting it to be done.

1.6 "Including", "in particular" etc. are illustrative only.

1.7 References to legislation include modifications, re‑enactments and subordinate legislation.

2. COMMENCEMENT & DURATION

This Agreement starts on the Commencement Date and continues for the On‑Air Period under the applicable Plan, unless terminated earlier under clause 12.

3. LICENCE, ACCESS & PERMITTED USE

3.1 Service licence. During the On‑Air Period and within the Territory, Snapshot Live grants the Customer a non‑exclusive, non‑transferable, non‑sublicensable right to access and use the Service for the Permitted Use.

3.2 Permitted Use (Images). Subject to this Agreement, the Customer may view, download and use Images: (a) for its internal business purposes (including audits, compliance and reporting); and (b) externally in the ordinary course of its business, including sharing with its clients, insurers, regulators and supply‑chain partners in connection with the relevant site or project. Public redistribution of bulk Images, creating a competing imagery database/service, or using Images to train Customer or third‑party foundational AI models is not permitted unless expressly agreed in Writing.

3.3 Image Retention. Images are retained on the Platform for up to 90 days from capture, after which they may be purged. The Customer may download/export Images within that period and may retain copies in its own systems for audit and compliance purposes. Snapshot Live is not responsible for Images not downloaded within the retention period and is not a records custodian for the Customer.

3.4 Image Consumption & Scheduling. Credit behaviour, capture ranges and rate limits are set out in Schedule 3 (Fair Use & Credit Accounting). The Customer acknowledges that high capture frequency may reduce battery life and impact on Service availability. Snapshot Live may apply fair‑use and rate limits to protect the Platform.

3.5 Restrictions. Except as expressly allowed, the Customer shall not: (a) resell, sublicense or make the Service or Images publicly available beyond the Permitted Use; (b) create a competing database/service using the Images or Platform; (c) remove or obscure watermarks, timestamps, attribution or embedded metadata; (d) reverse engineer, decompile or circumvent technical controls; (e) use the Service unlawfully or in breach of surveillance, privacy or data laws or this Agreement.

3.6 API & rate limits. Use of APIs and on‑demand functions is subject to fair‑use/rate limits (Schedule 3). Snapshot Live may throttle or suspend abusive calls.

3.7 Ownership & IPR. Snapshot Live and its licensors own all IPR in the Platform, firmware, AI models, system watermarks and documentation. The Customer owns its business data and any reports it compiles, Snapshot Live claims no ownership of the Customer's logos or third‑party content provided by the Customer.

3.8 Feedback. The Customer grants Snapshot Live a royalty‑free, transferable licence to use feedback and suggestions to improve the Service.

3.9 Surveillance compliance. The Customer is solely responsible for lawful deployment and use of Devices and Images (e.g., signage, policies, DPIAs where required, consultation, handling data subject rights). Snapshot Live accepts no liability for Customer non‑compliance.

3.10 AI analytics. AI outputs are probabilistic decision‑support only. Snapshot Live does not guarantee detection of any specific event/condition or any legal compliance outcome.

4. HARDWARE (DEVICES)

4.1 Supply. Devices may be sold or rented. Title in rented Devices remains with Snapshot Live. Risk passes on delivery. The Customer shall not remove serial numbers or modify firmware/hardware without consent.

4.2 Installation & maintenance. The Customer is responsible for safe mounting locations, permissions, health & safety compliance and site access. Devices require suitable sun exposure and cellular coverage. Routine maintenance includes cleaning of lens and solar panel. Environmental and connectivity conditions affect performance.

4.3 Hardware Warranty (24 months). Devices are warranted against material defects in materials and workmanship for 24 months from the Delivery Date when used in accordance with Snapshot Live's published operating parameters and fair‑use guidelines. Exclusions include: misuse, improper mounting, ingress beyond rated IP, vandalism, lightning/surge, unauthorised modifications/repairs, operation outside specified temperature/voltage ranges and damage caused by third‑party accessories or cabling.

4.4 Returns/RMA. Returns must follow Snapshot Live's RMA process (details on request). Dead‑on‑arrival issues must be reported within 14 days of delivery. Damaged or lost Devices may be replaced and credits/On‑Air time reallocated, refer to Schedule 1 for more details.

4.5 End‑of‑life (WEEE/Batteries). The Customer will not dispose of Devices, batteries or accessories with unsorted waste. Snapshot Live offers take‑back/recycling (clause 4.6).

4.6 Take‑Back Scheme. Snapshot Live will arrange return labels or direct the Customer to an approved collection/recycling partner.

4.7 Remote updates & safety. Snapshot Live may deploy security/firmware updates remotely and, where necessary for safety/security, temporarily disable Device functions.

5. API PROCESS AND INTEGRATION

5.1 API Access. Snapshot Live provides customers with the option to connect to its services via an application programming interface ("API"), subject to prior approval.

5.2 API Charges. API access is offered on a case-by-case basis and may be subject to additional charges, agreed upon on a "Price on Application" (POA) basis.

5.3 API Usage. Customers must use the API only in accordance with Snapshot Live's technical documentation, usage limits, and security requirements.

5.4 API Access Rights. Snapshot Live reserves the right to suspend, restrict, or revoke API access if it reasonably believes that the API is being misused, poses a security or stability risk, or breaches these Terms.

5.5 API Warranty. Snapshot Live makes no warranty that API access will be uninterrupted or error-free and accepts no liability for any damages, losses, or claims arising from the use of the API or customer integrations.

6. DELIVERY & ACCESS

Snapshot Live will use reasonable endeavours to meet Delivery Date(s). Access to the Platform is via credentials. The Customer is responsible for keeping credentials secure and for all use under its accounts.

7. FEES & PAYMENT

7.1 Fees (subscription/licence, Credits, Device sale/rental and professional services) are as set out in the quotation/order/invoice and payable in GBP unless stated otherwise. Fees exclude VAT and other sales taxes.

7.2 Invoices are payable within 30 days of the invoice date.

7.3 Payments shall be made without set‑off or deduction (save as required by law). If withholding is required, the Customer shall gross‑up the payment so Snapshot Live receives the amount it would have received absent withholding.

7.4 Late sums accrue interest under the Late Payment of Commercial Debts (Interest) Act 1998 and may incur statutory recovery costs. Snapshot Live may also suspend the Service under clause 13.4.

7.5 Credits. Credits are non‑refundable and must be used in line with the applicable Plan and On‑Air Period. Credit behaviour and accounting are set out in Schedule 3.

7.6 Price changes. Recurring fees may change at Plan renewal, Snapshot Live will provide at least 45 days' notice before the end of the then‑current term. Snapshot Live reserves the right to change Credit pricing at any time.

8. IPR NOTICES

All IPR in the Service, Platform, firmware, AI models and system watermarks remain with Snapshot Live. When distributing Images under the Permitted Use, the Customer shall not remove Snapshot Live's timestamps/watermarks or any agreed attribution.

9. WARRANTIES & SERVICE DISCLAIMER

9.1 Snapshot Live will provide the Service with reasonable skill and care.

9.2 Dependencies. Service quality depends on factors outside Snapshot Live's control (cellular coverage, weather, occlusions, vandalism, GPS availability, third‑party clouds). Uninterrupted availability is not warranted.

9.3 AI analytics. See clause 3.10.

9.4 Beta/Preview features. Any labelled beta/preview features are provided "as is" and may be modified or withdrawn.

9.5 Except as expressly stated, all warranties and conditions (statutory or implied) are excluded to the fullest extent permitted by law.

10. LIABILITY

10.1 Neither party is liable for loss of profit, revenue, contracts, goodwill, or for indirect or consequential loss.

10.2 Each party's total aggregate liability arising out of or in connection with this Agreement (whether in contract, tort, misrepresentation or otherwise) shall not exceed the total Fees paid or payable by the Customer in the 12 months preceding the event giving rise to the claim.

10.3 Nothing limits liability for death or personal injury caused by negligence, fraud, or any liability that cannot lawfully be limited.

11. INDEMNITIES

11.1 IPR indemnity. Snapshot Live will defend the Customer against third‑party claims that the unmodified Service (excluding Customer content, configurations or out‑of‑scope use) infringes UK IPR, and pay damages and costs finally awarded, provided the Customer promptly notifies Snapshot Live, gives sole control of the defence/settlement, and provides reasonable assistance.

11.2 If an infringement claim arises, Snapshot Live may: (a) modify the Service; (b) procure continued use; or (c) terminate the affected Service and refund prepaid fees for the unused period.

11.3 Customer indemnity. The Customer shall indemnify Snapshot Live against claims arising from (a) Customer data/content, (b) unlawful surveillance or processing, or (c) use of the Service in breach of this Agreement or law.

12. CONFIDENTIALITY

12.1 Each party will keep the other's Confidential Information confidential and use it only for performance of this Agreement.

12.2 Disclosure is allowed to employees/contractors/advisers under equivalent obligations. The obligations do not apply to information that is public (other than due to breach), already known without duty of confidence, independently developed, or required by law/regulator/court (with notice where lawful).

12.3 Neither party shall make public announcements about this Agreement without the other's consent, except that Snapshot Live may list the Customer's name and logo as a client unless the Customer opts out in Writing.

13. TERM, RENEWAL, SUSPENSION & TERMINATION

13.1 Term & Renewal. Each subscription commences on the Commencement Date for the Initial Term stated in the Plan. Unless either party gives written notice of non‑renewal at least 90 days before the end of the Initial Term or any Renewal Term, the subscription renews for successive periods equal to the Initial Term.

13.2 Termination for convenience. Not available mid‑term. Either party may elect not to renew under clause 13.1.

13.3 Termination for cause. Either party may terminate immediately on written notice if: (a) the other commits a material breach and fails to remedy within 14 days of notice; or (b) the other becomes insolvent or subject to an analogous event.

13.4 Suspension. Snapshot Live may suspend the Service (in whole or part) upon written notice if: (a) undisputed fees remain unpaid 14 days after notice of default; (b) Snapshot Live reasonably suspects unauthorised sharing or material breach; (c) the Device or use poses a security, safety or legal risk; or (d) required by law/regulator. Suspension will be lifted once the cause is remedied.

14. CONSEQUENCES OF TERMINATION

14.1 Accrued rights survive. All outstanding invoices (and Device rental charges to the date of return) become immediately due.

14.2 On termination: (a) Platform access ceases; (b) the Customer shall stop using the Service and return or destroy Snapshot Live Confidential Information; (c) the Customer may retain Images already lawfully downloaded solely for audit/compliance records related to the original Permitted Use; (d) rented Devices must be returned within 14 days in reasonable condition (fair wear and tear excepted) or replacement/repair fees may apply.

14.3 Clauses intended to survive (including 3.5–3.10, 4.7, 6, 7–11, 13, 15–21 and relevant Schedules) shall continue.

15. FORCE MAJEURE

No party is liable for delay or failure due to a Force Majeure Event, affected obligations are suspended for the duration of the event.

16. DATA PROTECTION

(SUMMARY — see Schedule 5 for full terms)

16.1 Roles. For Customer Personal Data, the Customer is Controller and Snapshot Live is Processor. Snapshot Live acts as independent Controller for limited account/billing and service‑operations data.

16.2 Instructions. Snapshot Live shall process Customer Personal Data only on documented instructions (including this Agreement, Customer configurations and API calls) and shall promptly inform the Customer if an instruction appears unlawful.

16.3 Security. Snapshot Live shall implement appropriate technical and organisational measures as described in Schedule 5 and may update them without materially reducing protection.

16.4 Subprocessors. Customer gives general authorisation for Subprocessors, changes will be notified with at least 30 days' opportunity to object. Snapshot Live remains responsible for Subprocessors.

16.5 Incidents. Snapshot Live shall notify the Customer without undue delay (and, where feasible, within 48 hours) after becoming aware of a Personal Data Breach affecting Customer Personal Data.

16.6 International transfers. Snapshot Live shall implement valid transfer mechanisms for restricted transfers (UK IDTA or UK Addendum to EU SCCs) as set out in Schedule 5 (Annex D).

16.7 Return & deletion. At termination, Snapshot Live will make a final export available and delete Customer Personal Data within 90 days (subject to legal retention and backup cycles).

16.8 Audit. Snapshot Live will maintain records and make available audit materials when Legally required to do so, audits may be conducted under Schedule 5 conditions.

17. GOVERNANCE, MONITORING & AUDIT

As in clause 16.8 and Schedule 5, Snapshot Live may also audit Customer use (remotely or on‑site during Business Days with reasonable notice) to verify compliance with clauses 3 and 11.

18. ASSIGNMENT & SUBCONTRACTING

18.1 The Customer may not assign or transfer this Agreement without Snapshot Live's written consent (not to be unreasonably withheld).

18.2 Snapshot Live may assign or subcontract its rights/obligations, Snapshot Live remains responsible for subcontractors.

19. COMPLIANCE

The parties shall comply with applicable export control and sanctions laws, the Bribery Act 2010 and the Modern Slavery Act 2015. The Customer warrants it will deploy Devices lawfully and safely.

20. ENTIRE AGREEMENT

This Agreement (including Schedules) is the entire agreement and supersedes all prior statements regarding its subject matter. Each party acknowledges it has not relied on statements not set out herein (fraud excepted).

21. VARIATIONS

Changes must be in Writing and signed/accepted by both parties (email acceptance is sufficient).

22. SEVERABILITY

If any provision is invalid/unenforceable, it will be modified to the minimum extent necessary, if impossible, it will be deleted without affecting the remainder.

23. THIRD‑PARTY RIGHTS

No third party has rights to enforce any term under the Contracts (Rights of Third Parties) Act 1999.

24. WAIVER

A delay or failure to exercise a right is not a waiver. A waiver must be in Writing and is specific to the instance waived.

25. NOTICES

25.1 Notices must be in Writing and sent to: Snapshot Live Limited, DOCK 2/006, 85 Explorations Drive, LE4 5NU, Leicester, United Kingdom and to legal@snapshot.live (or such other address notified in Writing).

25.2 Deemed delivery: (a) email—on sending if between 09:00–17:00 UK time on a Business Day (otherwise next Business Day); (b) personal delivery—on delivery; (c) Royal Mail first‑class recorded—09:00 on the second Business Day after posting; (d) international air post—09:00 on the fifth Business Day after posting.

26. RELATIONSHIP

The parties are independent contractors. Nothing creates a partnership, joint venture or agency.

27. LAW & JURISDICTION

27.1 This Agreement and any non‑contractual obligations are governed by the laws of England and Wales.

27.2 The courts of England and Wales have exclusive jurisdiction. (Optional arbitration clause for international deals may be added by Order.)


SCHEDULE 1 — HARDWARE REPLACEMENT & ACCESSORIES

Devices that are lost or structurally damaged may be replaced at cost. When the Customer notifies Snapshot Live, the affected Device will be deactivated within 48 hours. Remaining Credits and On‑Air time will be reallocated to a replacement Device when purchased.

Item                     Price (RRP)*
Solar Cellular Camera    £245.00 ex VAT
(SCC100 or SCC100-NA)
Solar Panel              £50.00 ex VAT
Clamp Set                £40.00 ex VAT
Charger                  £25.00 ex VAT

*Pricing Disclaimer: Prices are indicative only and may change due to reviews, supply chain or regulatory requirements. Contact us for an exact quotation before ordering.


SCHEDULE 2 — TERRITORIES

The hardware and Service are supported in:

- United Kingdom (UK)
- United States of America (USA)

(Additional countries may be added by notice. Use outside supported Territories is at the Customer's risk and not warranted.)


SCHEDULE 3 — FAIR USE & CREDIT ACCOUNTING

A. What consumes a Credit

1. Periodic Image: one Credit per successfully captured image during the Operating Window.
2. On‑Demand Image: one Credit per on‑demand request fulfilled during the Operating Window.
3. Failed captures: one Credit will not be deducted for a failed capture, however this does not include accidental or poor image capture, for example if an Image is captured during storage or transit.
4. AI analytics: baseline detection overlays with an image are included. Bespoke AI analytics pricing will be POA.
5. Derivatives/Thumbnails: platform thumbnails and watermarked previews do not consume Credits.
6. Exports/API pulls: exporting an already‑captured image does not consume a Credit. Generating a new image to satisfy an API request does.

B. Operating Window & minimum consumption

- The Device can be scheduled with a daily Operating Window (e.g., 12 hours/day).
- Within that window, Periodic capture may be set between every 15 minutes and every 4 hours.
- When activated, On‑Demand mode will be enabled automatically during the Operating Window only.
- Example, if the On-Demand mode is enabled and a desired 12 hour operating window is required, based on the maximum setting for a Periodic image capture, a minimum image consumption of one (1) image per four (4) hours applies (Periodic or On‑Demand), to maintain connectivity and system health.

C. Rate limits & Fair Use

- Snapshot Live may throttle excessive API calls or on‑demand bursts that risk platform stability.
- The Customer must not attempt to bypass Platform limits.

D. Expiry, transfers, pausing

- At the end of the On‑Air Period, the Device will stop capturing Images and the Credits will be suspended, until the On-Air Period is extended, unless otherwise stated in the Plan.
- Credits are non‑refundable and non‑transferable between legal entities.
- Snapshot Live may, at its discretion, allow a pause due to extended site shutdowns, pauses do not extend the original term unless agreed in Writing.

E. Negative balances & overspend

- If the Device exceeds its Credit balance, the Device will automatically suspend all new Image captures until a new set of Credit is purchased, unless an alternative method of Credit pooling or Credit management is pre-agreed in Writing.


SCHEDULE 4 — SUPPORT & SERVICE LEVELS (BASELINE)

- Support hours: Business Days 09:00–17:00 UK time by email/ticket.
- Target response: same Business Day for P1 (service down), next Business Day for P2/P3 (moderate impact).
- Maintenance windows: Snapshot Live may perform routine maintenance (usually off‑peak) and will use reasonable endeavours to provide advance notice where service impact is expected.
- Backups: Snapshot Live maintains standard backups for disaster recovery of the Platform, this is not a substitute for the Customer's own archiving obligations.
- No formal SLA is provided under these baseline terms, paid SLAs can be agreed in an Order if required.


SCHEDULE 5 — DATA PROCESSING

SCHEDULE (UK GDPR)

This Schedule forms the Parties' Data Processing Addendum and uses the definitions in clause 1 of the Agreement. If there is any conflict on data protection matters, this Schedule prevails.

1. Roles & Scope

1.1 Roles. For Customer Personal Data (including Images and related metadata), the Customer is Controller and Snapshot Live is Processor. Snapshot Live is independent Controller for limited account, billing and service-operations data it determines.

1.2 Scope. Snapshot Live will process Customer Personal Data only as necessary to provide the Service under the Agreement.

2. Instructions

Snapshot Live will process Customer Personal Data only on documented instructions from the Customer (including the Agreement, configurations and API calls) and will notify the Customer if an instruction appears unlawful (without providing legal advice).

3. Security

Snapshot Live will implement and maintain appropriate technical and organisational measures consistent with Article 32 UK GDPR, taking into account the state of the art, costs, and risks. A high-level Security Overview is available on request and may be updated without materially reducing protection. The Customer is responsible for lawful capture/use of Images (e.g., signage, DPIAs), secure configuration, user management and API key control.

4. Sub-processors

The Customer gives general authorisation for Snapshot Live to appoint sub-processors. Snapshot Live will remain responsible for their acts/omissions and will impose no-less-protective obligations by contract. Snapshot Live may change sub-processing arrangements from time to time and may provide general notice of material changes. If the Customer reasonably objects on data-protection grounds and no resolution is reached, the Customer may terminate the affected component with a pro-rata refund of prepaid, unused fees for that component.

5. International Transfers

Where Customer Personal Data is transferred outside the UK, Snapshot Live will ensure appropriate safeguards apply (e.g., the UK International Data Transfer Agreement or the UK Addendum to the EU 2021 SCCs, with supplementary measures where required).

6. Personal Data Breach

Snapshot Live will notify the Customer without undue delay after becoming aware of a Personal Data Breach affecting Customer Personal Data and will provide information then available together with measures taken or proposed. Snapshot Live will provide reasonable assistance with Customer regulatory and Data Subject notifications.

7. Assistance (DSRs & DPIAs)

Taking into account the nature of processing and information available, Snapshot Live will provide reasonable assistance to the Customer to respond to Data Subject requests and to conduct DPIAs/prior consultations where legally required.

8. Information & Audits (narrow, law-required)

Snapshot Live will make available information necessary to demonstrate compliance with Article 28. On-site audits/inspections will be permitted only where required by law or a competent Supervisory Authority and only where such compliance cannot reasonably be demonstrated by documentation, certificates, reports or meetings, and then subject to reasonable notice, confidentiality, scope and scheduling.

9. Return & Deletion

During the term the Customer may export Images/metadata via standard tools/APIs. On termination or written request, Snapshot Live will make a final export available and delete Customer Personal Data from active systems within 90 days, subject to legal retention and backup overwrite cycles.

10. Liability & Precedence

The Parties' liability under this Schedule is subject to the limitations/exclusions in the Agreement, save that nothing limits liability that cannot be limited by law. For data protection matters, this Schedule prevails over conflicting terms in the Agreement.

11. Changes

Snapshot Live may update this Schedule to reflect changes in law or supervisory guidance, providing notice unless earlier implementation is legally required.

Description of Processing (summary)

Subject matter & purpose: still-image capture, storage, image processing and data analytics, presentation/export to authorised users/APIs, and supporting operations (security, logging, billing).

Duration: from Commencement Date to termination/expiry plus the deletion period above.

Data subjects: individuals who may appear incidentally at Customer sites and Customer users.

Categories: Images with metadata (timestamps, GNSS locations, device IDs, technical logs), account data (name, business email, role, auth/activity), support communications.

Special categories: not intentionally processed, any incidental capture is Customer's responsibility to legitimise/mitigate.


SCHEDULE 8 — SIGNAGE RECOMMENDATIONS (GUIDANCE)

Non‑binding guidance to help Customers meet UK GDPR / DPA 2018 and CCTV best‑practice when deploying Snapshot Live Devices. This guidance does not constitute legal advice. Customers remain responsible for lawful deployment (see clauses 3.10 and 15).

A. Core principles (what your sign should tell people)

- That imaging is in operation (plain words and a pictogram e.g., camera icon).
- Purpose: e.g., "site safety, asset monitoring, incident investigation and compliance".
- Who is the data controller: your organisation's legal name (not Snapshot Live).
- How to contact the controller: a monitored email/phone and postal address.
- Retention: up to 90 days plus your longer on‑prem/business retention (if any).
- Rights: brief statement on access/objection/erasure.
- Processor reference: "Snapshot Live Limited processes images on our behalf."

B. Example template

IMAGES RECORDED AT INTERVALS

For site safety, asset monitoring and incident investigation.

Controller: [Your organisation name]

Contact: [email@yourdomain.com] | [01234 567890]

Processor: Snapshot Live Limited (on our behalf)

Retention: Images retained for up to 90 days.

C. Placement & readability

Place signs at site entrances, access gates, car parks and before any monitored area, with repeaters around the perimeter where routes differ. Mount them about 1.4–2.2 m high, unobstructed and, where practical, illuminated for night use. Use clear, high-contrast colours with headline text x-height ≥ 15–20 mm (readable at 5–10 m) and body text ≥ 5–7 mm (readable at 1–2 m).

D. Deployment tips (privacy by design)

- Aim the camera to minimise capture of neighbouring property and public areas.
- Operating Window: restrict capture to business need (e.g., working hours).
- Worker consultation: notify staff and contractors.
- DPIA: for higher‑risk deployments (public‑facing, extensive monitoring), complete a brief DPIA and document your Legitimate Interests Assessment (LIA).
- Children & sensitive areas: avoid monitoring toilets, changing rooms, prayer rooms or other sensitive spaces, add extra signage near schools/play areas.

© Snapshot Live Limited 2025`;

  const termsContent = isPilot ? pilotTermsContent : standardTermsContent;

  return (
    <>
      <div className={`border border-slate-200 rounded-lg p-4 bg-slate-50 ${className}`}>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="terms-acknowledgment"
              checked={acknowledged}
              onCheckedChange={handleCheckboxChange}
              className="mt-1"
            />
            <div className="flex-1">
              <label
                htmlFor="terms-acknowledgment"
                className="text-sm text-slate-700 cursor-pointer leading-relaxed"
              >
                I acknowledge and agree to the{" "}
                <button
                  type="button"
                  onClick={() => setShowTermsDialog(true)}
                  className="text-teal-600 hover:text-teal-700 underline font-medium"
                >
                  {isPilot ? "Pilot Pack Terms and Conditions" : "Terms and Conditions"}
                </button>
              </label>
            </div>
          </div>
          
          <div className="flex gap-2 ml-7">
          </div>
        </div>
      </div>

      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-4xl w-full h-[80vh] p-0 flex flex-col">
          <DialogHeader className="p-6 pb-0 flex-shrink-0">
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 px-6 pb-0 overflow-hidden">
            <div className="h-full overflow-y-auto border rounded-md p-4 bg-white text-sm leading-relaxed">
              <pre className="whitespace-pre-wrap font-sans">{termsContent}</pre>
            </div>
          </div>
          
          <div className="px-6 py-4 flex-shrink-0 border-t bg-slate-50">
            <Button onClick={downloadPdf} variant="outline" className="w-full">
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
